import { FC, ReactNode } from 'react';

import type { Document } from '@/shared/api/transcriptor';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  transcriptorModel,
  type TranscriptorConsultation,
} from '@/entities/transcriptor';

import {
  isDictation,
  isLiveProtocolDocument,
  liveSession,
  useLiveSession,
} from '@/features/audioRecording';

import type { EncounterHeading } from '../config';
import { toAnswerEdit, toReleaseEdit, type UnresolvedNoteLine } from '../lib';

import {
  LiveProtocolDocument,
  StoredProtocolDocument,
  type ProtocolPhase,
} from './LiveProtocolDocument';
import type { NoteAssumptionsReport } from './NoteAssumptions';

type Props = {
  encounter: TranscriptorConsultation;
  document: Document;
  canEdit: boolean;
  // Kept for documents without structured answers, e.g. a letter.
  fallback: ReactNode;
  className?: string;
  onMissingRequiredChange?: (titles: string[]) => void;
  onUnresolvedNoteLinesChange?: (lines: UnresolvedNoteLine[]) => void;
  onNoteAssumptionsChange?: (report: NoteAssumptionsReport) => void;
  encounterHeading?: EncounterHeading;
};

// A protocol of an encounter: the live note while the session that recorded
// it holds its answers, otherwise the answers stored on the server.
export const EncounterProtocolDocument: FC<Props> = ({
  encounter,
  document,
  canEdit,
  fallback,
  className,
  onMissingRequiredChange,
  onUnresolvedNoteLinesChange,
  onNoteAssumptionsChange,
  encounterHeading,
}) => {
  const dispatch = useAppDispatch();
  const session = useLiveSession();
  const recording = useAppSelector((state) => state.transcriptor.recording);

  if (
    !isLiveProtocolDocument(document) &&
    !(
      encounter.transcriptorV2 &&
      document.type === 'summary' &&
      document.id &&
      document.template_id
    )
  ) {
    return <>{fallback}</>;
  }
  const finished =
    document.status === 'completed' || document.status === 'edited';
  const generating =
    document.status === 'pending' || document.status === 'generating';
  if (!finished && !generating) {
    return <>{fallback}</>;
  }
  const documentId = document.id ?? '';
  const live = session?.meta.encounterId === encounter.id ? session.live : null;
  const recordingThis =
    !!live &&
    recording.consultationId === encounter.id &&
    (recording.isRecording || recording.isUploading);
  // A finished protocol of an encounter that is still processing stays
  // read-only: the server can re-render it only once processing is over. A
  // dictation's note is finished by scribe after Stop too.
  const settled =
    encounter.status === 'succeeded' || encounter.status === 'failed';
  const phase: ProtocolPhase =
    finished && settled ? 'done' : recordingThis ? 'recording' : 'processing';
  const editable =
    canEdit && (phase === 'recording' || encounter.status === 'succeeded');
  const liveDocument = live?.documents[documentId];

  // An edit of a finished protocol re-renders the document and withdraws its
  // approval on the server, so the encounter is read again.
  const refreshFinished = () => {
    if (encounter.status === 'succeeded') {
      void dispatch(
        transcriptorModel.thunks.fetchConsultationById({
          consultationId: encounter.id,
          patientId: encounter.patientId,
        }),
      );
    }
  };

  if (!live || !liveDocument || liveDocument.finalFailed) {
    return (
      <StoredProtocolDocument
        key={documentId}
        className={className}
        encounterId={encounter.id}
        document={document}
        phase={phase}
        canEdit={editable}
        fallback={fallback}
        onSaved={refreshFinished}
        onMissingRequiredChange={onMissingRequiredChange}
        onUnresolvedNoteLinesChange={onUnresolvedNoteLinesChange}
        onNoteAssumptionsChange={onNoteAssumptionsChange}
        encounterHeading={encounterHeading}
      />
    );
  }
  return (
    <LiveProtocolDocument
      key={documentId}
      className={className}
      document={document}
      phase={phase}
      fillsLive={isDictation(live)}
      state={liveDocument}
      chunks={live.chunks}
      canEdit={editable}
      onAnswer={(template, key, value) => {
        const edit = toAnswerEdit({ template, key, value });
        if (edit) {
          liveSession.edit(documentId, edit);
        }
      }}
      onRelease={(template, key) => {
        const edit = toReleaseEdit({ template, key });
        if (edit) {
          liveSession.edit(documentId, edit);
        }
      }}
      onMissingRequiredChange={onMissingRequiredChange}
      onNoteEdit={(edit) => liveSession.edit(documentId, edit)}
      onReadAnswers={() => void liveSession.readAnswers(documentId)}
      onUnresolvedNoteLinesChange={onUnresolvedNoteLinesChange}
      onNoteAssumptionsChange={onNoteAssumptionsChange}
      encounterHeading={encounterHeading}
    />
  );
};
